import {
    createSlice,
    createEntityAdapter,
    createSelector,
    createAsyncThunk
  } from '@reduxjs/toolkit'
  
  /*
  import { apiSlice } from '../api/apiSlice'

// in order for the initial fetch to happen this call needs to be made in index.js
// store.dispatch(extendedApiSlice.endpoints.getCalls.initiate())

  export const callsAdapter = createEntityAdapter({
    selectId: (call) => call._id,
    sortComparer: (a, b) => a.time.localeCompare(b.time),
  })

  const initialState = callsAdapter.getInitialState()

  export const extendedApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
      getCalls: builder.query({
            query: ({shortName,filterType=0,filterCode=0}) => ({
              url: `/${shortName}/calls`,
              method: "GET", 
              params: { filterType, filterCode },
            }),
        transformResponse: responseData => {
            responseData = responseData.calls;
          return callsAdapter.setAll(initialState, responseData)
        }
      })
    })
  })
  

  // from tutorials: https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#transforming-responses
  export const { useGetCallsQuery } = extendedApiSlice
// Calling `someEndpoint.select(someArg)` generates a new selector that will return
// the query result object for a query with those parameters.
// To generate a selector for a specific query argument, call `select(theQueryArg)`.
// In this case, the users query has no params, so we don't pass anything to select()
export const selectCallsResult = extendedApiSlice.endpoints.getCalls.select({"shortName":"test"})

const selectCallsData = createSelector(
  selectCallsResult,
  (callsResult) => {
    return callsResult.data
  }
)

export const { selectAll: selectAllCalls, selectById: selectCallById } = callsAdapter.getSelectors(state => { 
    return selectCallsData(state) ?? initialState
})
*/
const now = new Date().getTime();

export const callsAdapter = createEntityAdapter({
  selectId: (call) => call._id,
  sortComparer: (a, b) => b.time.localeCompare(a.time),
})

const initialState = {
    loading: false,
    data: callsAdapter.getInitialState(),
    oldestCallTime: now,
    newestCallTime: now,
    // Cleared when a page of calls comes back with nothing new in it, so the
    // infinite-scroll sentinels stop asking for more once an end is reached.
    hasMoreOlder: true,
    hasMoreNewer: true,
    // Identifies the list currently being shown. A page of older/newer calls
    // that was requested against a previous filter is dropped when it lands.
    requestVersion: 0,
    // Bumped when a page of newer calls is prepended, and when the list is
    // replaced. useLiveScroll watches these to tell a paged-in block of calls
    // (which must not move the page) from a live call arriving (which may).
    prependVersion: 0,
    resetVersion: 0
  }

  // The adapter keeps ids sorted newest-first, so the bounds we page from are
  // always the ends of the list.
  function updateCallTimes(state) {
    if (state.data && (state.data.ids.length > 0)) {
      const first = state.data.ids[0]
      const firstTime = state.data.entities[first].time;
      const last = state.data.ids[state.data.ids.length-1];
      const lastTime = state.data.entities[last].time;
      state.newestCallTime = new Date(firstTime).getTime();
      state.oldestCallTime = new Date(lastTime).getTime();
    }
  }

  function buildUrlParams(a) {
    return Object.keys(a).map(function(k) {
         return encodeURIComponent(k) + '=' + encodeURIComponent(a[k])
     }).join('&')
   }

  function buildCallUrl(getState, direction=false, date=false) {
      var params = {};
      var url = "";
  
      const {shortName,filterType,filterGroupId, filterTalkgroups, filterUnit, filterStarred, filterDate, filterCallId, filterRows, filterTimeframe} = getState().callPlayer;
      const hasTimeframe = filterTimeframe && (filterTimeframe.from || filterTimeframe.to);

      if ((typeof direction === 'string') && (typeof date === 'number')) {
          url = url + '/' + direction;
          params["time"] = date;
      } else  if (filterCallId) {
        url = url + '/date'
        params["time"] = filterDate;
      } else if (filterDate){
          url = url + '/older'
          params["time"] = filterDate;
        }

      if (filterStarred) {
        params["filter-starred"] = true;
      }
      // A stacked, SIEM-style filter is sent as one JSON param and supersedes
      // the legacy single filter-type/filter-code pair. The optional From/To
      // range rides along in the same spec.
      if ((filterRows && filterRows.length > 0) || hasTimeframe) {
        const spec = { rows: filterRows || [] };
        if (hasTimeframe) {
          spec.timeframe = { from: filterTimeframe.from || 0, to: filterTimeframe.to || 0 };
        }
        params["filter"] = JSON.stringify(spec);
        url = process.env.REACT_APP_BACKEND_SERVER + "/" + shortName + "/calls" + url + '?' + buildUrlParams(params);
        console.log("Trying to fetch data from this url: " + url);
        return url;
      }
      switch(filterType) {
        case 1:
        params["filter-type"] = "group";
        params["filter-code"] = filterGroupId;
        break;
        case 2:
        params["filter-type"] = "talkgroup";
        params["filter-code"] = filterTalkgroups;
        break;
        case 3:
        params["filter-type"] = "unit";
        params["filter-code"] = filterUnit;
        break;
        default:
        case 0:
      }
  /*
      if (this.filterCode != "") {
          params["filter-code"] = this.filterCode;
      }
      if (this.filterType == this.FilterType.Group) {
          params["filter-type"] = "group";
          params["filter-name"] = this.filterName;
      }
      if (this.filterType == this.FilterType.Talkgroup) {
          params["filter-type"] = "talkgroup";
      }
      if (this.filterType == this.FilterType.Unit) {
          params["filter-type"] = "unit";
      }
  */
    //  url = startUp.backend_server + "/" + startUp.shortName.toLowerCase() + "/calls" + url + '?' + buildUrlParams(params);
      url = process.env.REACT_APP_BACKEND_SERVER + "/" + shortName + "/calls" + url + '?' + buildUrlParams(params);
      console.log("Trying to fetch data from this url: " + url);
      return url;
  }

// The next call autoplay should move on to when one finishes: the closest call
// *newer* than it that has not been played yet.
//
// The list is ordered by when a call started, but calls reach us when they
// finish uploading - so a long call lands well below the short ones that
// started after it and got here first. Stepping blindly to the row above would
// therefore replay a call the listener already heard, and then the one above
// that, walking back up through the whole run.
export const nextCallToPlay = (callsData, currentCallId) => {
  if (!callsData) return null;

  const index = callsData.ids.findIndex((callId) => callId === currentCallId);
  if (index < 0) return null;

  for (let i = index - 1; i >= 0; i--) {
    const call = callsData.entities[callsData.ids[i]];
    if (call && !call.played) {
      return call;
    }
  }
  return null;
}

export const addStar = createAsyncThunk(
  'calls/addStar',
  async(callId,{getState, requestIdleCallback}) => {
    const url = process.env.REACT_APP_BACKEND_SERVER + "/add_star/" + callId
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: "{}"
    
  }).then(
    (data) => data.json()
  )
  return res;
  }
)

export const removeStar = createAsyncThunk(
  'calls/removeStar',
  async(callId,{getState, requestIdleCallback}) => {
    const url = process.env.REACT_APP_BACKEND_SERVER + "/remove_star/" + callId
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: "{}"
    
  }).then(
    (data) => data.json()
  )
  return res;
  }
)

// Sets the alias for a Radio ID (rid) - used to replace an "N/A" source tag
// in the call pane with a real unit name. This is a global lookup, not
// per-call, so a successful update is applied to every source across every
// loaded call that shares that rid (see setRidTag.fulfilled below).
export const setRidTag = createAsyncThunk(
  'calls/setRidTag',
  async({rid, tag},{getState, requestIdleCallback}) => {
    const url = process.env.REACT_APP_BACKEND_SERVER + "/radio_ids/" + rid + "/tag"
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ tag })

  }).then(
    (data) => data.json()
  )
  return res;
  }
)

export const getCalls = createAsyncThunk(
  'calls/getCalls',
  async({},{getState, requestId}) => {
    // getCalls.pending has already bumped requestVersion by the time we get
    // here, so this stamps the response with the list it belongs to.
    const {requestVersion} = getState().calls;
    const url = buildCallUrl(getState);
    const res = await fetch(url).then(
      (data) => data.json()
    )
    return {...res, requestVersion};
  })

  export const getOlderCalls = createAsyncThunk(
    'calls/getOlderCalls',
    async({},{getState, requestId} ) => {
      const {oldestCallTime, requestVersion} = getState().calls;
      const url = buildCallUrl(getState,"older",oldestCallTime);
      const res = await fetch(url).then(
        (data) => data.json()
      )
      return {...res, requestVersion};
    },
    {
      // One page at a time: the sentinel can come back into view while a fetch
      // is still out, and a second request would page from the same timestamp.
      condition: (arg, {getState}) => !getState().calls.loading
    })

    export const getNewerCalls = createAsyncThunk(
      'calls/getNewerCalls',
      async({},{getState, requestId} ) => {
        const {newestCallTime, requestVersion} = getState().calls;
        const url = buildCallUrl(getState,"newer",newestCallTime);
        const res = await fetch(url).then(
          (data) => data.json()
        )
        return {...res, requestVersion};
      },
      {
        condition: (arg, {getState}) => !getState().calls.loading
      })

export const callsSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    addCall: (state, action) => {
      const call = {...action.payload, played: false}
      state.data = callsAdapter.addOne(state.data, call)
      updateCallTimes(state);
    },
    playedCall: (state,action) => {
      state.data = callsAdapter.updateOne(state.data,{id: action.payload, changes: {played: true}});
    },
    setTranscript: (state, action) => {
      state.data = callsAdapter.updateOne(state.data, { id: action.payload._id, changes: { transcript: action.payload.transcript } });
    }
  },
  extraReducers: {
    [getCalls.pending]: (state) => {
      state.loading = true;
      state.requestVersion += 1;
    },
    [getCalls.rejected]: (state) => {
      state.loading = false;
    },
    [getCalls.fulfilled]: (state, {payload}) => {
      state.loading = false;
      // A newer request replaced this one while it was in flight.
      if (payload.requestVersion !== state.requestVersion) {
        return;
      }
      const calls = payload.calls.map( (call) => ({...call, played: false}));
      state.data = callsAdapter.setAll(state.data, calls)
      state.hasMoreOlder = calls.length > 0;
      state.hasMoreNewer = true;
      state.resetVersion += 1;
      updateCallTimes(state);
    },
    [getOlderCalls.pending]: (state) => {
      state.loading = true;
    },
    [getOlderCalls.rejected]: (state) => {
      state.loading = false;
    },
    [getOlderCalls.fulfilled]: (state, {payload}) => {
      state.loading = false;
      if (payload.requestVersion !== state.requestVersion) {
        return;
      }
      const before = state.data.ids.length;
      const calls = payload.calls.map( (call) => ({...call, played: false}));
      state.data = callsAdapter.addMany(state.data, calls)
      // A page that added nothing means we have reached the end of what the
      // filter can return - stop the sentinel asking for the same page forever.
      state.hasMoreOlder = state.data.ids.length > before;
      updateCallTimes(state);
    },
    [getNewerCalls.pending]: (state) => {
      state.loading = true;
    },
    [getNewerCalls.rejected]: (state) => {
      state.loading = false;
    },
    [getNewerCalls.fulfilled]: (state, {payload}) => {
      state.loading = false;
      if (payload.requestVersion !== state.requestVersion) {
        return;
      }
      const before = state.data.ids.length;
      const calls = payload.calls.map( (call) => ({...call, played: false}));
      state.data = callsAdapter.addMany(state.data, calls)
      state.hasMoreNewer = state.data.ids.length > before;
      if (state.data.ids.length > before) {
        // These rows land above whatever the user is reading; tell the scroll
        // logic to hold their position rather than let the page jump.
        state.prependVersion += 1;
      }
      updateCallTimes(state);
    },
    [addStar.fulfilled]: (state, {payload}) => {
      state.data.entities[payload.call._id] = payload.call;
    },
    [removeStar.fulfilled]: (state, {payload}) => {
      state.data.entities[payload.call._id] = payload.call;
    },
    [setRidTag.fulfilled]: (state, {payload}) => {
      if (!payload.success) {
        return;
      }
      // A rid's alias is global, so patch every loaded call whose srcList references it.
      Object.values(state.data.entities).forEach((call) => {
        if (call && call.srcList) {
          call.srcList.forEach((source) => {
            if (String(source.src) === String(payload.rid)) {
              source.tag = payload.tag;
            }
          });
        }
      });
    }
  }
})
export const { addCall, playedCall, setTranscript } = callsSlice.actions
export const callsReducer = callsSlice.reducer;
export default callsReducer;
